import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { merchantHistoryQueryKey } from '@/hooks/useMerchantHistory';
import { MerchantRecord } from '@/shared/types/merchant';
import {
  contactFieldErrors,
  MERCHANT_CONTACT_LABELS,
  sameEmail,
  type ManageMerchantContactsResponse,
  type MerchantContact,
  type MerchantContactField,
  type MerchantContactInput,
} from '@/shared/types/merchantContacts';
import { callMerchantContacts, merchantContactsQueryKey, type MerchantContactsRequest } from '@/utils/organization/merchantContacts';
import { EditButton } from './EditButton';
import { SettingsSection } from './SettingsGroup';

// Merchant Contacts (FIG-320 / REQ-027, FIG-345 item 5 · UC-228). Every person
// at the merchant, with exactly one Point of Contact (POC). The POC's email is
// the merchant's sign-in email, so changing it (promoting another contact, or
// editing the POC's email) asks first, removes the former address's access and
// emails a magic link to the new one. All writes go through
// manage-merchant-contacts-v1, which records them in the merchant's history.

export const MERCHANT_CONTACTS_SECTION_ID = 'merchant-contacts';

interface MerchantContactsSectionProps {
  merchant: MerchantRecord;
  /** An admin with admin mode on. */
  canEdit: boolean;
  /** Whether the viewer may read the contacts list (Figabl admins). Defaults to `canEdit`. */
  canView?: boolean;
  /** Called after the POC changed, so the merchant record (Merchant Details, header, Overview) reloads. */
  onUpdated?: () => void;
}

type Editing = { kind: 'new' } | { kind: 'edit'; contact: MerchantContact } | null;

type Notice =
  | { kind: 'saved'; recorded: boolean }
  | { kind: 'link'; sent: boolean; email: string; recorded: boolean };

type Confirm =
  | { kind: 'promote'; contact: MerchantContact }
  | { kind: 'remove'; contact: MerchantContact }
  | { kind: 'sign-in-email'; contact: MerchantContact; input: MerchantContactInput };

const EMPTY_INPUT: MerchantContactInput = { name: '', role: '', email: '', phone: '', slack: '' };

const toInput = (contact: MerchantContact): MerchantContactInput => ({
  name: contact.name,
  role: contact.role ?? '',
  email: contact.email,
  phone: contact.phone ?? '',
  slack: contact.slack ?? '',
});

const dash = (value: string | null | undefined) => (value && value.trim() ? value : '—');

/** The POC from organisations.poc_* when the list can't be read (non-admins, or the call failed). */
const fallbackContacts = (merchant: MerchantRecord): MerchantContact[] =>
  merchant.poc_name || merchant.poc_email
    ? [{
      id: 'poc',
      organisation_id: merchant.id,
      name: merchant.poc_name ?? '',
      role: null,
      email: merchant.poc_email ?? '',
      phone: merchant.poc_phone ?? null,
      slack: null,
      is_poc: true,
      created_at: '',
      updated_at: '',
    }]
    : [];

export const MerchantContactsSection: React.FC<MerchantContactsSectionProps> = ({ merchant, canEdit, canView, onUpdated }) => {
  const queryClient = useQueryClient();
  const mayRead = canView ?? canEdit;
  const queryKey = merchantContactsQueryKey(merchant.id);
  const contactsQuery = useQuery({
    queryKey,
    enabled: mayRead,
    retry: false,
    queryFn: async () => {
      const result = await callMerchantContacts({ action: 'list', organisationId: merchant.id });
      if (!result.success || !Array.isArray(result.contacts)) throw new Error(result.error ?? 'Contacts could not be loaded');
      return result.contacts;
    },
  });

  const [editing, setEditing] = useState<Editing>(null);
  const [draft, setDraft] = useState<MerchantContactInput>(EMPTY_INPUT);
  const [errors, setErrors] = useState<Partial<Record<MerchantContactField, string>>>({});
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  // "Saved" fades after a few seconds (REQ-025); a link that wasn't sent stays until dismissed.
  useEffect(() => {
    if (!notice || (notice.kind === 'link' && !notice.sent)) return;
    const timer = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(timer);
  }, [notice]);

  const loaded = contactsQuery.isSuccess;
  const contacts = loaded ? contactsQuery.data : fallbackContacts(merchant);
  const editable = canEdit && loaded;
  const currentPoc = contacts.find((c) => c.is_poc);

  const startEdit = (next: Editing) => {
    setEditing(next);
    setDraft(next?.kind === 'edit' ? toInput(next.contact) : EMPTY_INPUT);
    setErrors({});
    setActionError(null);
    setNotice(null);
  };

  const apply = async (request: MerchantContactsRequest, options: { pocChanged?: boolean } = {}): Promise<ManageMerchantContactsResponse> => {
    setSaving(true);
    setActionError(null);
    try {
      const result = await callMerchantContacts(request);
      if (!result.success) {
        if (result.fieldErrors) setErrors(result.fieldErrors);
        setActionError(result.error ?? 'Not saved. Please try again.');
        return result;
      }
      // A saved change whose reload failed: refetch rather than show a stale or empty list.
      if (result.contacts) queryClient.setQueryData(queryKey, result.contacts);
      else await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: merchantHistoryQueryKey(merchant.id) });
      const recorded = result.recorded !== false;
      setNotice(result.linkEmail !== undefined
        ? { kind: 'link', sent: result.linkSent === true, email: result.linkEmail, recorded }
        : { kind: 'saved', recorded });
      if (options.pocChanged || result.linkEmail !== undefined) onUpdated?.();
      return result;
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async (confirmed = false) => {
    const validation = contactFieldErrors(draft);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    if (editing?.kind === 'edit') {
      const { contact } = editing;
      if (contact.is_poc && !sameEmail(contact.email, draft.email) && !confirmed) {
        setConfirm({ kind: 'sign-in-email', contact, input: draft });
        return;
      }
      const result = await apply(
        { action: 'update', organisationId: merchant.id, contactId: contact.id, contact: draft },
        { pocChanged: contact.is_poc },
      );
      if (result.success) setEditing(null);
      return;
    }
    const result = await apply({ action: 'add', organisationId: merchant.id, contact: draft });
    if (result.success) setEditing(null);
  };

  const runConfirmed = async (current: Confirm) => {
    setConfirm(null);
    if (current.kind === 'promote') {
      await apply({ action: 'promote', organisationId: merchant.id, contactId: current.contact.id }, { pocChanged: true });
    } else if (current.kind === 'remove') {
      await apply({ action: 'remove', organisationId: merchant.id, contactId: current.contact.id });
    } else {
      await saveDraft(true);
    }
  };

  const update = (field: MerchantContactField) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDraft((d) => ({ ...d, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const editor = editing && (
    <div className="mt-4 space-y-4 rounded-md bg-muted/40 p-4" data-testid="contact-editor">
      <h4 className="text-sm font-semibold">{editing.kind === 'new' ? 'Add contact' : `Edit ${editing.contact.name || editing.contact.email}`}</h4>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {(['name', 'role', 'email', 'phone', 'slack'] as MerchantContactField[]).map((field) => {
          const id = `contact-${field}`;
          const required = field === 'name' || field === 'email';
          return (
            <div key={field} className="space-y-1.5">
              <Label htmlFor={id}>{MERCHANT_CONTACT_LABELS[field]}{required ? ' *' : ''}</Label>
              <Input
                id={id}
                type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                placeholder={field === 'phone' ? '+14155550123' : field === 'slack' ? '@name or channel' : undefined}
                value={draft[field] ?? ''}
                onChange={update(field)}
                aria-invalid={!!errors[field]}
                aria-describedby={errors[field] ? `${id}-error` : undefined}
              />
              {errors[field] && <p id={`${id}-error`} className="text-xs text-destructive">{errors[field]}</p>}
            </div>
          );
        })}
      </div>
      {editing.kind === 'edit' && editing.contact.is_poc && (
        <p className="text-sm text-muted-foreground">
          This is the Point of Contact. Changing the email changes the merchant's sign-in email.
        </p>
      )}
      {actionError && <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">{actionError}</div>}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" onClick={() => void saveDraft()} disabled={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {editing.kind === 'new' ? 'Add contact' : 'Save contact'}
        </Button>
        <Button type="button" variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
      </div>
    </div>
  );

  return (
    <SettingsSection
      id={MERCHANT_CONTACTS_SECTION_ID}
      title="Merchant Contacts"
      description="People at this merchant. The Point of Contact's email is the merchant's sign-in email."
      action={editable && !editing ? (
        <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => startEdit({ kind: 'new' })}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add contact
        </Button>
      ) : null}
    >
      {notice && (
        <div
          role="status"
          className={notice.kind === 'link' && !notice.sent
            ? 'mb-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900'
            : 'mb-3 text-sm text-emerald-700'}
        >
          {notice.kind === 'saved' && (notice.recorded ? 'Saved' : "Saved, but not recorded in the merchant's history")}
          {notice.kind === 'link' && notice.sent && `Saved. A magic link was emailed to ${notice.email}.`}
          {notice.kind === 'link' && !notice.sent && (
            <>
              <span className="flex-1">Saved, but the magic link was not sent to {notice.email}.</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={saving}
                onClick={() => void apply({ action: 'resend_link', organisationId: merchant.id })}
              >
                Resend magic link
              </Button>
            </>
          )}
        </div>
      )}
      {mayRead && contactsQuery.isError && (
        <div role="alert" className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
          <span className="flex-1">Contacts couldn't be loaded. Showing the Point of Contact only.</span>
          <Button type="button" variant="outline" size="sm" onClick={() => void contactsQuery.refetch()}>Try again</Button>
        </div>
      )}
      {!editing && actionError && (
        <div role="alert" className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">{actionError}</div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Slack</TableHead>
            {editable && <TableHead><span className="sr-only">Actions</span></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {mayRead && contactsQuery.isLoading ? (
            <TableRow>
              <TableCell colSpan={editable ? 5 : 4} className="text-center text-muted-foreground">
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden="true" />Loading contacts…
              </TableCell>
            </TableRow>
          ) : contacts.length > 0 ? contacts.map((contact) => (
            <TableRow key={contact.id} data-testid={`contact-row-${contact.email}`}>
              <TableCell>
                <div className="font-medium">{dash(contact.name)}</div>
                <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  {contact.role && <span>{contact.role}</span>}
                  {contact.is_poc && <Badge variant="secondary">Point of Contact</Badge>}
                </div>
              </TableCell>
              <TableCell>{dash(contact.email)}</TableCell>
              <TableCell>{contact.phone || 'Not provided'}</TableCell>
              <TableCell className={contact.slack ? undefined : 'text-muted-foreground'}>{dash(contact.slack)}</TableCell>
              {editable && (
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    {!editing && <EditButton label={`Edit ${contact.name || contact.email}`} onClick={() => startEdit({ kind: 'edit', contact })} />}
                    {!editing && !contact.is_poc && (
                      <>
                        <Button type="button" variant="ghost" size="sm" disabled={saving} onClick={() => setConfirm({ kind: 'promote', contact })}>
                          Make POC
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          disabled={saving}
                          aria-label={`Remove ${contact.name || contact.email}`}
                          onClick={() => setConfirm({ kind: 'remove', contact })}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          )) : (
            <TableRow>
              <TableCell colSpan={editable ? 5 : 4} className="text-center text-muted-foreground">No contacts yet.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {editor}

      <AlertDialog open={!!confirm} onOpenChange={(open) => { if (!open) setConfirm(null); }}>
        <AlertDialogContent>
          {confirm?.kind === 'promote' && (
            <AlertDialogHeader>
              <AlertDialogTitle>Make {confirm.contact.name || confirm.contact.email} the Point of Contact?</AlertDialogTitle>
              <AlertDialogDescription>
                {confirm.contact.email} becomes {merchant.name}'s sign-in email, and a magic link is emailed to it.
                {currentPoc ? ` ${currentPoc.name || currentPoc.email} stays as a contact but can no longer sign in to this merchant.` : ''}
              </AlertDialogDescription>
            </AlertDialogHeader>
          )}
          {confirm?.kind === 'remove' && (
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {confirm.contact.name || confirm.contact.email}?</AlertDialogTitle>
              <AlertDialogDescription>They are removed from {merchant.name}'s contacts. This is recorded in the history.</AlertDialogDescription>
            </AlertDialogHeader>
          )}
          {confirm?.kind === 'sign-in-email' && (
            <AlertDialogHeader>
              <AlertDialogTitle>Change the sign-in email?</AlertDialogTitle>
              <AlertDialogDescription>
                {merchant.name} will sign in with {confirm.input.email.trim()} instead of {confirm.contact.email}, and a magic
                link is emailed to the new address. The old address can no longer sign in to this merchant.
              </AlertDialogDescription>
            </AlertDialogHeader>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (confirm) void runConfirmed(confirm); }}>
              {confirm?.kind === 'promote' ? 'Make POC' : confirm?.kind === 'remove' ? 'Remove' : 'Change sign-in email'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsSection>
  );
};

export default MerchantContactsSection;
